// Calculation Update WebSocket service for real-time calculation progress tracking - shared singleton
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';

interface CalculationProgress {
  roomId: string;
  totalJobs: number;
  processedJobs: number;
  status: string;
  updatedBy: string;
}

interface CalculationFailed {
  roomId: string;
  jobId: string;
  errorMessage: string;
  status: string;
  updatedBy: string;
}

interface CalculationMessage {
  type: string;
  data: CalculationProgress;
  timestamp: string;
}

class CalculationWebSocketService {
  private static instance: CalculationWebSocketService | null = null;
  private messageHandlers: ((message: CalculationMessage) => void)[] = [];
  private isInitialized: boolean = false;
  private socket: WebSocket | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private heartbeatInterval: NodeJS.Timeout | null = null; // Heartbeat interval for connection health
  private isAuthenticated = false; // Flag to track authentication status
  private userStore: any = null; // Store reference to UserStore instance
  private environment = import.meta.env.VITE_ENVIRONMENT;

  // Observable state - tracks progress for multiple rooms
  roomProgress: Map<string, CalculationProgress> = new Map();
  roomFailures: Map<string, CalculationFailed> = new Map(); // Track calculation failures
  lastMessage: CalculationMessage | null = null;
  connectionError: string | null = null;
  isConnected: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  static getInstance(): CalculationWebSocketService {
    if (!CalculationWebSocketService.instance) {
      CalculationWebSocketService.instance = new CalculationWebSocketService();
    }
    return CalculationWebSocketService.instance;
  }

  // Method to set the UserStore instance
  setUserStore(userStore: any): void {
    this.userStore = userStore;
  }

  /**
   * Initialize the calculation WebSocket service
   * This connects to the calculation-update WebSocket endpoint for all rooms
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    try {
      this.connect();
      runInAction(() => {
        this.isInitialized = true;
        this.connectionError = null;
      });

    } catch (error) {
      runInAction(() => {
        this.connectionError = 'Failed to initialize WebSocket service';
      });
    }
  }

  /**
   * Get the WebSocket URL for calculation updates
   */
  private getWebSocketUrl(): string {
    let backendUrl;
    
    if (import.meta.env.VITE_MODE === 'dev') {
      backendUrl = import.meta.env.VITE_BACKEND_SOCKET_ENDPOINT;
    } else {
      backendUrl = (window as any).config?.backendSocketEndpoint || (window as any).config?.backendBaseUrl;
    }
    
    // Fallback to localhost:8000 if no backend URL is configured
    if (!backendUrl) {
      backendUrl = 'http://localhost:8000';
    }
    
    if (backendUrl?.endsWith('/')) {
      backendUrl = backendUrl.slice(0, -1);
    }
    
    const wsUrl = backendUrl?.replace('https://', 'wss://').replace('http://', 'ws://');
    const fullUrl = `${wsUrl}/ws/calculation-update/`;
    
    return fullUrl;
  }

  /**
   * Connect to the calculation-update WebSocket endpoint
   */
  private connect(): void {
    try {
      const wsUrl = this.getWebSocketUrl();
      
      this.socket = new WebSocket(wsUrl);
      
      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
    } catch (error) {
      runInAction(() => {
        this.connectionError = 'Failed to connect to WebSocket';
      });
    }
  }

  /**
   * Handle WebSocket connection open
   */
  private handleOpen(event: Event): void {
    runInAction(() => {
      this.isConnected = true;
      this.connectionError = null;
      this.reconnectAttempts = 0;
    });
    
    // Send "Connection Successful" message
    this.sendMessage({ type: 'connection_successful' });
    
    // Start heartbeat to keep connection alive during long periods
    this.startHeartbeat();
  }

  /**
   * Handle WebSocket message
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      // Handle connection establishment message
      if (data.type === 'connection_established') {
        // Send user authentication details after connection is established
        this.sendUserAuthenticationDetails();
      }
      
      // Handle authentication response
      if (data.type === 'authentication_response') {
        if (data.authenticated) {
          this.isAuthenticated = true;
        } else {
          this.isAuthenticated = false;
          // Close connection if authentication fails
          this.cleanup();
        }
      }

      // Handle different message types
      if (data.type === 'calculation_message' && data.message) {
        const message = data.message;
        
        switch (message.type) {
          case 'calculation_started':
            this.handleCalculationStarted(message);
            break;
          case 'calculation_progress':
            this.handleCalculationProgress(message);
            break;
          case 'calculation_completed':
            this.handleCalculationCompleted(message);
            break;
          case 'calculation_failed':
            this.handleCalculationFailed(message);
            break;
        }
      }

      // Notify message handlers
      this.messageHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          if (this.environment === 'development') {
            console.error('Error in calculation message handler:', error);
          }
          toast.error('Error in calculation message handler!');
        }
      });

    } catch (error) {
      if (this.environment === 'development') {
        console.error('Error parsing calculation WebSocket message:', error);
      }
      toast.error('Error parsing calculation WebSocket message!');
    }
  }

  /**
   * Handle WebSocket connection close
   */
  private handleClose(event: CloseEvent): void {
    runInAction(() => {
      this.isConnected = false;
    });

    this.isAuthenticated = false; // Reset authentication status

    // Stop heartbeat on disconnect
    this.stopHeartbeat();

    // Attempt to reconnect if not a normal closure
    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      
      setTimeout(() => {
        this.connect();
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  /**
   * Handle WebSocket error
   */
  private handleError(event: Event): void {
    runInAction(() => {
      this.connectionError = 'WebSocket connection error';
    });
  }

  /**
   * Send message to WebSocket
   */
  private sendMessage(message: any): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * Handle calculation started message
   */
  private handleCalculationStarted(message: any): void {
    runInAction(() => {
      // Clear any existing failure state when calculation starts
      this.roomFailures.delete(message.data.roomId);
      this.roomProgress.set(message.data.roomId, message.data);
    });
  }

  /**
   * Handle calculation progress message
   */
  private handleCalculationProgress(message: any): void {
    runInAction(() => {
      this.roomProgress.set(message.data.roomId, message.data);
    });
  }

  /**
   * Handle calculation completed message
   */
  private handleCalculationCompleted(message: any): void {
    runInAction(() => {
      // Keep the completed progress for a short time, then remove it
      this.roomProgress.set(message.data.roomId, message.data);
      
      // Remove the progress after a delay to allow UI to show completion
      setTimeout(() => {
        runInAction(() => {
          this.roomProgress.delete(message.data.roomId);
        });
      }, 3000); // 3 seconds delay
    });
  }

  /**
   * Handle calculation failed message
   */
  private handleCalculationFailed(message: any): void {
    runInAction(() => {
      // Store the failure state for the room
      this.roomFailures.set(message.data.roomId, message.data);
      
      // Remove any existing progress for this room
      this.roomProgress.delete(message.data.roomId);
    });
  }

  /**
   * Add message handler
   */
  addMessageHandler(handler: (message: CalculationMessage) => void): void {
    this.messageHandlers.push(handler);
  }

  /**
   * Remove message handler
   */
  removeMessageHandler(handler: (message: CalculationMessage) => void): void {
    const index = this.messageHandlers.indexOf(handler);
    if (index > -1) {
      this.messageHandlers.splice(index, 1);
    }
  }

  /**
   * Get progress for a specific room
   */
  getRoomProgress(roomId: string): CalculationProgress | undefined {
    return this.roomProgress.get(roomId);
  }

  /**
   * Get progress percentage for a specific room
   */
  getProgressPercentage(roomId: string): number {
    const progress = this.roomProgress.get(roomId);
    if (!progress || progress.totalJobs === 0) {
      return 0;
    }
    return Math.round((progress.processedJobs / progress.totalJobs) * 100);
  }

  /**
   * Get status message for a specific room
   */
  getStatusMessage(roomId: string): string {
    const progress = this.roomProgress.get(roomId);
    return progress?.status || '';
  }

  /**
   * Get progress text for a specific room
   */
  getProgressText(roomId: string): string {
    const progress = this.roomProgress.get(roomId);
    if (!progress) {
      return '';
    }
    return `${progress.processedJobs}/${progress.totalJobs}`;
  }

  /**
   * Check if a specific room is currently active
   */
  get isRoomActive(): (roomId: string) => boolean {
    return (roomId: string) => {
      const progress = this.roomProgress.get(roomId);
      return progress !== undefined;
    };
  }

  /**
   * Check if a specific room calculation is completed
   */
  get isRoomCompleted(): (roomId: string) => boolean {
    return (roomId: string) => {
      const progress = this.roomProgress.get(roomId);
      return progress !== undefined && progress.processedJobs === progress.totalJobs && progress.totalJobs > 0;
    };
  }

  /**
   * Check if any room is currently active
   */
  get isAnyRoomActive(): boolean {
    return this.roomProgress.size > 0;
  }

  /**
   * Reset progress for a specific room
   */
  resetRoomProgress(roomId: string): void {
    runInAction(() => {
      this.roomProgress.delete(roomId);
    });
  }

  /**
   * Reset all progress
   */
  resetAllProgress(): void {
    runInAction(() => {
      this.roomProgress.clear();
      this.roomFailures.clear();
    });
  }

  /**
   * Reset failure for a specific room
   */
  resetRoomFailure(roomId: string): void {
    runInAction(() => {
      this.roomFailures.delete(roomId);
    });
  }

  /**
   * Get failure for a specific room
   */
  getRoomFailure(roomId: string): CalculationFailed | undefined {
    return this.roomFailures.get(roomId);
  }

  /**
   * Check if a specific room has calculation failure
   */
  get isRoomFailed(): (roomId: string) => boolean {
    return (roomId: string) => {
      return this.roomFailures.has(roomId);
    };
  }

  /**
   * Start heartbeat to keep connection alive during long periods of inactivity
   * Sends heartbeat messages every 30 seconds to prevent connection timeout
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing heartbeat
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.isAuthenticated && this.socket) {
        this.socket.send(JSON.stringify({ type: 'heartbeat' }));
      }
    }, 30000); // Send heartbeat every 30 seconds
  }

  /**
   * Stop heartbeat interval
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Send user authentication details to the server
   */
  private sendUserAuthenticationDetails(): void {
    // Check if we have access to UserStore
    if (!this.userStore) {
      this.sendMessage({ 
        type: 'authentication_failed', 
        message: 'UserStore not available' 
      });
      return;
    }

    // Check if user data is currently being loaded
    if (this.userStore.isUserInfoLoading) {
      // Retry authentication after 1 second
      setTimeout(() => {
        if (this.isConnected && !this.isAuthenticated) {
          this.sendUserAuthenticationDetails();
        }
      }, 1000);
      
      return;
    }

    // Check if user metadata is available
    if (!this.userStore.userMetadata) {
      // Retry authentication after 2 seconds
      setTimeout(() => {
        if (this.isConnected && !this.isAuthenticated) {
          this.sendUserAuthenticationDetails();
        }
      }, 2000);
      
      return;
    }

    try {
      const userInfo = this.userStore.userMetadata.userInfo;
      
      // Additional check to ensure userInfo exists
      if (!userInfo) {
        setTimeout(() => {
          if (this.isConnected && !this.isAuthenticated) {
            this.sendUserAuthenticationDetails();
          }
        }, 2000);
        
        return;
      }

      const userDetails = {
        type: 'user_authentication',
        user_id: userInfo?.id || '',
        email: userInfo?.email || '',
        name: userInfo?.name || '',
        department: userInfo?.deptId?.departmentCode || '',
        department_name: userInfo?.deptId?.departmentName || '',
        roles: this.userStore.userModulesRoles?.pps || [],
        timestamp: new Date().toISOString()
      };

      this.sendMessage(userDetails);
    } catch (error) {
      this.sendMessage({ 
        type: 'authentication_failed', 
        message: 'Error preparing user details' 
      });
    }
  }

  /**
   * Cleanup the calculation WebSocket service
   */
  cleanup(): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      // Stop heartbeat
      this.stopHeartbeat();
      
      // Close WebSocket connection
      if (this.socket) {
        this.socket.close(1000, 'Service cleanup');
        this.socket = null;
      }
      
      // Clear message handlers
      this.messageHandlers = [];
      
      runInAction(() => {
        this.isInitialized = false;
        this.isConnected = false;
        this.roomProgress.clear();
        this.roomFailures.clear();
        this.lastMessage = null;
        this.connectionError = null;
      });

      this.isAuthenticated = false; // Reset authentication status
    } catch (error) {
      if (this.environment === 'development') {
        console.error('Error cleaning up Calculation WebSocket service:', error);
      }
      toast.error('Error cleaning up Calculation WebSocket service!');
    }
  }

  /**
   * Check if user is authenticated
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }
}

// Export class and singleton instance
export { CalculationWebSocketService };
export const calculationWebSocketService = CalculationWebSocketService.getInstance();
export default calculationWebSocketService;
